import { useTranslation } from "react-i18next";

import { trackEvent } from "@lib/infrastructure";
import { HaNViewStoreContext } from "@lib/logic";
import { getAllBuildings, getDefaultBuilding, useAppSelector } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { useModal } from "@hooks";
import { SingleSelectCard } from "@organisms";

import { HaNContext } from "./HaNContext";

/** Opens the building picker, with the default building first. `null` when there's only one building. */
export const useOpenBuildingPicker = () => {
  const { t } = useTranslation();
  const { addPages, openModal } = useModal();
  const { building } = useRequiredContext(HaNContext);
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const buildings = useAppSelector(getAllBuildings);
  const defaultBuildingId = useAppSelector(getDefaultBuilding)?.id;

  if (buildings.length <= 1) return null;

  const isDefault = (buildingId: string) => {
    return Number(buildingId === defaultBuildingId);
  };

  const entities = [...buildings]
    .sort((left, right) => isDefault(right.id) - isDefault(left.id))
    .map(candidate => ({ id: candidate.id, label: candidate.name }));

  const chooseBuilding = (buildingId: string) => {
    trackEvent("HereAndNow__ChangeBuilding", { id: buildingId });

    building.selectBuilding(buildingId);
    viewStore.deselectAll();
  };

  const openBuildingPicker = () => {
    addPages([
      <SingleSelectCard
        key="building-picker"
        defaultId={defaultBuildingId ?? ""}
        preselectedId={building.buildingId ?? ""}
        headerTitle={t("screen:MeetingBuildingSelection")}
        entities={entities}
        onSelect={chooseBuilding}
      />,
    ]);
    openModal();
  };

  return openBuildingPicker;
};
